/**
 * Formats a chat date string to a human-readable relative time
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatChatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

/**
 * Formats a history timestamp with model name
 * @param {string} timestamp - ISO timestamp string
 * @param {string} model - Model name
 * @returns {string} - Formatted timestamp with model name
 */
export const formatHistoryTimestamp = (timestamp, model) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let dateStr;
  if (diffDays === 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    dateStr = `${displayHours}:${displayMinutes}${ampm}`;
  } else if (diffDays === 1) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    dateStr = `Yesterday ${displayHours}:${displayMinutes}${ampm}`;
  } else {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    dateStr = `${day} ${month} ${displayHours}:${displayMinutes}${ampm}`;
  }

  return `${model} - ${dateStr}`;
};

/**
 * Returns only the time/date part used in history (e.g. "10:37am", "Yesterday 10:37am")
 * Used for "Executive Summary (Model) – 10:37am" style labels.
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} - Formatted time/date string
 */
export const getHistoryDateStr = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${displayHours}:${displayMinutes}${ampm}`;
  }
  if (diffDays === 1) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `Yesterday ${displayHours}:${displayMinutes}${ampm}`;
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${day} ${month} ${displayHours}:${displayMinutes}${ampm}`;
};

/**
 * Groups chats by date into categories: today, yesterday, previous7Days, older
 * @param {Array} chats - Array of chat objects
 * @returns {Object} - Object with grouped chats
 */
export const groupChatsByDate = (chats) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const grouped = {
    today: [],
    yesterday: [],
    previous7Days: [],
    older: []
  };

  chats.forEach(chat => {
    const dateString = chat.createdAt || chat.updatedAt;
    if (!dateString) {
      grouped.older.push(chat);
      return;
    }

    const chatDate = new Date(dateString);
    if (isNaN(chatDate.getTime())) {
      grouped.older.push(chat);
      return;
    }

    const chatDateOnly = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

    if (chatDateOnly.getTime() === today.getTime()) {
      grouped.today.push(chat);
    } else if (chatDateOnly.getTime() === yesterday.getTime()) {
      grouped.yesterday.push(chat);
    } else if (chatDateOnly.getTime() >= sevenDaysAgo.getTime()) {
      grouped.previous7Days.push(chat);
    } else {
      grouped.older.push(chat);
    }
  });

  return grouped;
};

